/**
 * Global Session Manager
 * 
 * Manages session state to prevent cascading 401 errors when token expires.
 * When session is marked as expired, API calls are blocked until user re-authenticates.
 */

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired')
    this.name = 'SessionExpiredError'
  }
}

type SessionState = 'active' | 'expired' | 'refreshing'
type SessionListener = (state: SessionState) => void

class SessionManager {
  private state: SessionState = 'active'
  private listeners: Set<SessionListener> = new Set()
  private expiredAt: number | null = null
  
  // Debounce: ignore rapid session expiry signals within this window
  private readonly EXPIRY_DEBOUNCE_MS = 2000
  
  getState(): SessionState {
    return this.state
  }
  
  isExpired(): boolean {
    return this.state === 'expired'
  }
  
  isRefreshing(): boolean {
    return this.state === 'refreshing'
  }
  
  isActive(): boolean {
    return this.state === 'active'
  }
  
  /**
   * Mark session as expired (e.g., after token refresh fails)
   * Debounced to prevent multiple rapid expiry signals
   */
  markExpired(): void {
    const now = Date.now()
    
    // Debounce: if we already marked expired recently, ignore
    if (this.expiredAt && (now - this.expiredAt) < this.EXPIRY_DEBOUNCE_MS) {
      return
    }
    
    if (this.state !== 'expired') {
      console.warn('🔴 Session marked as expired')
      this.state = 'expired'
      this.expiredAt = now
      this.notifyListeners()
    }
  }
  
  /**
   * Mark session as refreshing (token refresh in progress)
   */
  markRefreshing(): void {
    if (this.state === 'active') {
      if (import.meta.env.DEV) console.log('🔄 Session marked as refreshing')
      this.state = 'refreshing'
      this.notifyListeners()
    }
  }
  
  /**
   * Mark session as active (e.g., after successful login or token refresh)
   */
  markActive(): void {
    if (this.state !== 'active') {
      if (import.meta.env.DEV) console.log('🟢 Session marked as active')
      this.state = 'active'
      this.expiredAt = null
      this.notifyListeners()
    }
  }
  
  /**
   * Reset session state (e.g., when user clicks login)
   */
  reset(): void {
    this.state = 'active'
    this.expiredAt = null
    this.notifyListeners()
  }
  
  /**
   * Subscribe to session state changes
   */
  subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }
  
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.state)
      } catch (e) {
        console.error('Session listener error:', e)
      }
    })
  }
}

// Singleton instance
export const sessionManager = new SessionManager()

// React hook for session state
import { useState, useEffect } from 'react'

export function useSessionState(): SessionState {
  const [state, setState] = useState<SessionState>(sessionManager.getState())
  
  useEffect(() => {
    return sessionManager.subscribe(setState)
  }, [])
  
  return state
}

export function useIsSessionExpired(): boolean {
  const state = useSessionState()
  return state === 'expired'
}
